import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchNotifications, markSeen, markRead, markAllRead } from './api';

// One key, one poll. The feed response carries the rows AND the unseen badge
// count, so the badge, the dropdown list, and the web toast all share a single
// query instead of hitting separate endpoints.
export const FEED_KEY = ['/notifications'];

/**
 * Dismiss every notification the OS has already delivered for this app.
 *
 * Call this whenever the notifications surface OPENS — not off a mark-seen /
 * mark-read mutation. The tray is per-DEVICE; `seenAt` is per-USER on the
 * server and only ever flips once. Read the feed anywhere else (the web app,
 * a second device, an earlier session) and `unseenCount` is already 0, so the
 * mutations never fire — while this device's tray still holds the
 * notifications and the launcher keeps its badge. Gating on a server latch
 * cannot clear a device-local tray; opening the surface can.
 *
 * Atlas has no Capacitor access of its own (its standalone setup was removed
 * when it moved onto the native-app-shell), so this goes through the runtime
 * bridge. Reached raw rather than via the connector because the installed
 * connector (0.5.0) predates the seam and its ShellRuntime type has no such
 * field; swap for the connector's `clearDeliveredNotifications` once a version
 * carrying it is published.
 *
 * Safe to call unconditionally and repeatedly — idempotent, no-ops on web and
 * on shell binaries that predate the seam, and never throws (a stale icon, not
 * a failed read).
 */
export async function clearDeliveredNotifications(): Promise<void> {
  const push = (globalThis as { __shellRuntime?: { push?: { clearDelivered?: () => Promise<void> } } })
    .__shellRuntime?.push;
  if (typeof push?.clearDelivered !== 'function') return;
  try {
    await push.clearDelivered();
  } catch {
    /* best-effort */
  }
}

export function useNotificationFeed() {
  return useQuery({
    queryKey: FEED_KEY,
    queryFn: () => fetchNotifications(),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

export function useMarkSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markSeen(),
    onSettled: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markRead(id),
    onSettled: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markAllRead(),
    onSettled: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });
}
